import { NIL as NIL_UUID } from 'uuid';
import { tFunnelsWithStages } from '../Pipe/Pipe.types';

/**
 * RET-2331: the Directory's funnel and stage filter, shared by the Embudo and
 * Etapa column headers and the top of the Filtros panel. One funnel, then one
 * stage of that funnel, as the previous Directory had it. 'all' means no
 * filter; the nil uuid as funnel is Bandeja de entrada, which the server
 * filters as `inbox`, so it has no stages to pick.
 */
export const ALL_FILTER = 'all';

export type tFilterOption = { value: string; label: string };

export type tFunnelOptionLabels = {
  /** "Todos los embudos": the first option, which clears the filter. */
  all: string;
  /** The nil-uuid funnel's name, "Bandeja de entrada". */
  inbox: string;
};

export function funnelFilterOptions(
  funnels: tFunnelsWithStages[] | null | undefined,
  labels: tFunnelOptionLabels,
): tFilterOption[] {
  return [
    { value: ALL_FILTER, label: labels.all },
    ...(funnels ?? []).map((funnel) => ({
      value: funnel.id,
      label: funnel.id === NIL_UUID ? labels.inbox : funnel.name,
    })),
  ];
}

/** A stage can be picked once a real funnel, not the inbox, is chosen. */
export function canFilterByStage(
  funnels: tFunnelsWithStages[] | null | undefined,
  funnelId: string,
): boolean {
  if (funnelId === ALL_FILTER || funnelId === NIL_UUID) return false;
  return Boolean(funnels?.some((funnel) => funnel.id === funnelId));
}

export type tStageOptionsInput = {
  funnels: tFunnelsWithStages[] | null | undefined;
  funnelId: string;
  /** "Todas las etapas": the first option, which clears the stage. */
  allLabel: string;
};

/** The chosen funnel's stages; the inbox stage every pipe carries is left out. */
export function stageFilterOptions(input: tStageOptionsInput): tFilterOption[] {
  const { funnels, funnelId, allLabel } = input;
  const all = { value: ALL_FILTER, label: allLabel };
  if (!canFilterByStage(funnels, funnelId)) return [all];

  const funnel = funnels?.find((candidate) => candidate.id === funnelId);
  return [
    all,
    ...(funnel?.stages ?? [])
      .filter((stage) => stage.id !== NIL_UUID)
      .map((stage) => ({ value: stage.id, label: stage.name })),
  ];
}

export type tFunnelFilterValue = { funnel: string; stage: string };

/**
 * A funnel and stage known to exist, or 'all' in their place: a saved view can
 * name a funnel or stage deleted since, and the server refuses an unknown one
 * (422) instead of ignoring it. Without the funnels loaded yet, the pair is
 * kept as is; the list request itself is the check then.
 */
export function knownFunnelFilter(
  funnels: tFunnelsWithStages[] | null | undefined,
  value: tFunnelFilterValue,
): tFunnelFilterValue {
  if (!funnels?.length) return value;
  const funnel = funnels.find((candidate) => candidate.id === value.funnel);
  if (!funnel) return { funnel: ALL_FILTER, stage: ALL_FILTER };
  const stageKnown = funnel.stages.some((stage) => stage.id === value.stage);
  return { funnel: funnel.id, stage: stageKnown ? value.stage : ALL_FILTER };
}
